import type { BasePlaneState } from './tools/implementations/line-tool/SelectionContextProvider';
import type { IViewConfig } from './types';
import type { ToolSettingsSchema, ToolSettingsChangeEvent } from './ToolSettings';
import type { DragEventData } from './BaseTool';
import type { ToolUndoRedoManager } from './tools/ToolUndoRedoManager';

/**
 * The kinds of input a subtool can accept. A host tool routes DOM input to its
 * active subtools filtered by each subtool's current mask, so the host can mute
 * a specific kind (e.g. suppress a draw subtool's pointer input while an
 * interactive camera-align gesture is in progress) without the subtool's
 * cooperation.
 */
export type SubToolInputKind = 'pointer' | 'click' | 'keyboard' | 'wheel';

/** Default mask: a subtool accepts every input kind when enabled. */
export const ALL_SUBTOOL_INPUT: ReadonlySet<SubToolInputKind> = new Set<SubToolInputKind>([
  'pointer',
  'click',
  'keyboard',
  'wheel',
]);

/**
 * A reusable interaction unit hosted by a tool. Unlike an `ITool`, a subtool is
 * never registered with a persona's toolbar and never binds its own DOM
 * listeners — its host (an `ITool`) owns the lifecycle and forwards input.
 *
 * This generalizes the LineTool ↔ mode relationship up one level: a host tool
 * owns one or more subtools (e.g. a draw subtool), supplies them the base plane
 * to draw on, and routes input to them per their `inputMask`. Multiple subtools
 * can be active under one host simultaneously.
 *
 * `LineTool` implements this and is only ever driven by a host.
 *
 * Pointer/click/keyboard/wheel handlers mirror the `ITool` signatures so a host
 * can forward verbatim. A handler may return `true` to mark the event handled
 * and stop the host from routing it to lower-priority subtools.
 */
export interface ISubTool {
  /** Stable identifier, unique within a host. */
  readonly name: string;

  /** Begin interacting. The host calls this when it wants the subtool live. */
  activate(): void;

  /** Stop interacting and release transient visual state. */
  deactivate(): void;

  /**
   * The input kinds this subtool currently accepts. The host intersects this
   * with its own per-subtool mute set before routing. Implementations that
   * always accept everything can return `ALL_SUBTOOL_INPUT`.
   */
  getInputMask(): ReadonlySet<SubToolInputKind>;

  /**
   * Update the plane the subtool draws on. The host derives this from its
   * selection context (floor plane for floorplan hosts, surface plane for
   * surface hosts) and pushes it on activate + whenever the selection changes.
   * `null` means "no valid selection — idle".
   */
  setBasePlane(state: BasePlaneState | null): void;

  /**
   * The host owns DOM input + drag detection and forwards its drag state here
   * each move, so subtools that need it (e.g. to suppress hover during a camera
   * orbit) see the right value. Optional — subtools that don't care omit it.
   */
  setHostDragging?(dragging: boolean): void;

  /** Perform undo on the subtool's own stack (host routes Ctrl+Z here). */
  performUndo?(): boolean;
  /** Perform redo on the subtool's own stack (host routes Ctrl+Y here). */
  performRedo?(): boolean;
  /** The subtool's undo/redo manager, if it keeps one. */
  readonly undoRedo?: ToolUndoRedoManager | null;

  // Input — all optional; a host forwards only the kinds in the mask.
  // Returning true marks the event handled (stops host propagation).
  onMouseDown?(event: MouseEvent): boolean | void;
  onMouseMove?(event: MouseEvent): boolean | void;
  onMouseUp?(event: MouseEvent): boolean | void;
  onClick?(event: MouseEvent): boolean | void;
  onDoubleClick?(event: MouseEvent): boolean | void;
  onRightClick?(event: MouseEvent): boolean | void;
  onKeyDown?(event: KeyboardEvent): boolean | void;
  onKeyUp?(event: KeyboardEvent): boolean | void;
  onWheel?(event: WheelEvent): boolean | void;

  // Drag callbacks — the host owns drag detection (via BaseTool) and forwards
  // the detected drag here. Used by the MeasureSubTool to edit existing
  // measurements (drag a vertex, drag an edge to insert a point).
  onDragStart?(data: DragEventData): void;
  onDrag?(data: DragEventData): void;
  onDragEnd?(data: DragEventData): void;

  // Config passthroughs — a host may surface these in its own view/settings.
  getViewConfig?(): IViewConfig;
  getSettingsSchema?(): ToolSettingsSchema;
  onSettingsChange?(event: ToolSettingsChangeEvent): void;

  /** Release all resources. Called when the host is disposed. */
  dispose(): void;
}
